import React, { useEffect, useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import toast from 'react-hot-toast';
import { serviceRequestService } from '../../services/serviceRequestService';

const statusOptions = [
  { value: 'pending', label: 'Pending', className: 'bg-yellow-100 text-yellow-700' },
  { value: 'in-progress', label: 'In Progress', className: 'bg-blue-100 text-blue-700' },
  { value: 'completed', label: 'Completed', className: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', className: 'bg-red-100 text-red-700' },
];

const getStatusMeta = (status) => {
  return statusOptions.find((s) => s.value === status) || {
    value: status,
    label: status,
    className: 'bg-gray-100 text-gray-700',
  };
};

const ServiceRequests = () => {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);

  const safeArray = (value) => (Array.isArray(value) ? value : []);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    fetchRequests();
  }, []);

  const fetchRequests = async () => {
    try {
      const res = await serviceRequestService.getAll();
      // serviceRequestService.getAll returns response.data
      setRequests(res?.data || res || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load service requests');
    } finally {
      setLoading(false);
    }
  };

  const filtered = useMemo(() => {
    const reqArr = Array.isArray(requests) ? requests : [];
    const q = search.trim().toLowerCase();
    if (!q) return reqArr;
    return reqArr.filter((r) => {
      return (
        r._id?.toString().toLowerCase().includes(q) ||
        r.name?.toLowerCase().includes(q) ||
        r.email?.toLowerCase().includes(q) ||
        r.serviceType?.toLowerCase().includes(q)
      );
    });
  }, [requests, search]);

  const updateStatus = async (id, status) => {
    try {
      await serviceRequestService.updateStatus(id, status);
      toast.success('Status updated');
      fetchRequests();
    } catch (e) {
      toast.error('Failed to update status');
    }
  };

  if (loading) return <div className="text-center py-8">Loading...</div>;

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Service Requests</h1>
        <span className="px-4 py-2 bg-purple-100 text-purple-700 rounded-xl font-medium">
          Total: {requests.length}
        </span>
      </div>

      <div className="mb-6">
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name/email/service..."
          className="w-full pl-4 pr-4 py-3 border border-gray-200 rounded-xl focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
        />
      </div>

      <div className="bg-white/80 backdrop-blur-xl rounded-2xl shadow-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gradient-to-r from-purple-600 to-blue-600 text-white">
              <tr>
                <th className="px-6 py-4 text-left">Request</th>
                <th className="px-6 py-4 text-left">Customer</th>
                <th className="px-6 py-4 text-left">Service</th>
                <th className="px-6 py-4 text-left">Priority</th>
                <th className="px-6 py-4 text-left">Status</th>
                <th className="px-6 py-4 text-left">Date</th>
                <th className="px-6 py-4 text-center">Details</th>
              </tr>
            </thead>

            <tbody>
              {filtered.map((r) => {
                const meta = getStatusMeta(r.status);
                return (
                  <tr key={r._id} className="border-b hover:bg-purple-50 transition-colors">
                    <td className="px-6 py-4 font-mono text-sm text-gray-600">{r._id?.slice(-8)}</td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-800">{r.name}</div>
                      <div className="text-sm text-gray-500">{r.email}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-medium text-gray-800">{r.serviceType}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className="px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-700">
                        {r.priority}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <select
                        value={r.status}
                        onChange={(e) => updateStatus(r._id, e.target.value)}
                        className={`px-3 py-1 rounded-full text-sm font-medium outline-none cursor-pointer ${meta.className}`}
                      >
                        {statusOptions.map((s) => (
                          <option key={s.value} value={s.value}>
                            {s.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-600">{new Date(r.createdAt).toLocaleDateString()}</td>
                    <td className="px-6 py-4 text-center">
                      <button
                        onClick={() => setSelected(r)}
                        className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
                      >
                        <Eye size={18} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>

          {filtered.length === 0 && (
            <div className="p-6 text-center text-gray-500">No service requests found.</div>
          )}
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full p-8 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={() => setSelected(null)}
              className="absolute top-4 right-4 p-2 hover:bg-gray-100 rounded-xl transition-colors"
            >
              ×
            </button>

            <h2 className="text-2xl font-bold text-gray-800 mb-6">Service Request Details</h2>

            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-gray-500">Request ID</div>
                  <div className="font-mono text-gray-800">{selected._id}</div>
                </div>
                <div>
                  <div className="text-gray-500">Status</div>
                  <div className={`inline-block px-3 py-1 rounded-full text-sm font-medium ${getStatusMeta(selected.status).className}`}>
                    {getStatusMeta(selected.status).label}
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <div className="text-gray-500">Name</div>
                  <div className="font-medium text-gray-800">{selected.name}</div>
                </div>
                <div>
                  <div className="text-gray-500">Phone</div>
                  <div className="font-medium text-gray-800">{selected.phone}</div>
                </div>
              </div>

              <div>
                <div className="text-gray-500">Email</div>
                <div className="font-medium text-gray-800">{selected.email}</div>
              </div>

              <div>
                <div className="text-gray-500">Service</div>
                <div className="font-medium text-gray-800">{selected.serviceType}</div>
              </div>

              <div>
                <div className="text-gray-500">Priority</div>
                <div className="font-medium text-gray-800">{selected.priority}</div>
              </div>

              <div>
                <div className="text-gray-500">Message</div>
                <div className="bg-gray-50 p-4 rounded-xl text-gray-700 whitespace-pre-wrap">{selected.message || '-'}</div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ServiceRequests;

